// 랜딩 페이지 - 오로라 배경과 아름다운 교회 실루엣
// "은혜 가운데 함께 성장하는 공동체"

import React from "react";
import { useNavigate } from "react-router-dom";
import { AuroraBackground } from "../components/ui/AuroraBackground";
import {
    FiUsers,
    FiHome,
    FiBook,
    FiDollarSign,
    FiArrowRight,
} from "react-icons/fi";

// 핵심 기능 데이터
const features = [
    {
        icon: FiUsers,
        title: "교적 관리",
        description: "성도님들의 정보를 체계적으로 관리하고 한눈에 파악하세요.",
        gradient: "from-blue-500 to-cyan-400",
    },
    {
        icon: FiHome,
        title: "심방 관리",
        description: "심방 일정과 기록을 효율적으로 관리하여 양육에 집중하세요.",
        gradient: "from-purple-500 to-pink-400",
    },
    {
        icon: FiBook,
        title: "교육/양육 관리",
        description: "양육 과정과 수강생을 체계적으로 관리하세요.",
        gradient: "from-emerald-500 to-teal-400",
    },
    {
        icon: FiDollarSign,
        title: "재정 관리",
        description: "헌금 및 지출 내역을 투명하고 정확하게 기록하세요.",
        gradient: "from-amber-500 to-orange-400",
    },
];

// 기능 카드 컴포넌트
const FeatureCard: React.FC<{
    icon: React.ElementType;
    title: string;
    description: string;
    gradient: string;
    delay: number;
}> = ({ icon: Icon, title, description, gradient, delay }) => {
    return (
        <div
            className="group relative p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 
                 hover:bg-white/20 hover:border-white/30 transition-all duration-500 
                 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
            style={{ animationDelay: `${delay}ms` }}
        >
            {/* 아이콘 배경 */}
            <div
                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} 
                    flex items-center justify-center mb-4 
                    group-hover:scale-110 transition-transform duration-300
                    shadow-lg`}
            >
                <Icon className="w-7 h-7 text-white" />
            </div>

            {/* 제목 */}
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>

            {/* 설명 */}
            <p className="text-gray-300 text-sm leading-relaxed">{description}</p>

            {/* 호버 시 나타나는 화살표 */}
            <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <FiArrowRight className="w-5 h-5 text-white/70" />
            </div>
        </div>
    );
};

// 십자가 빨간 불빛 컴포넌트
const CrossLight: React.FC = () => {
    return (
        <div className="absolute top-[22%] left-1/2 transform -translate-x-1/2 z-20">
            {/* 빨간 불빛 - 반짝반짝 애니메이션 */}
            <div
                className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-red-500 
                   animate-red-glow"
            />
        </div>
    );
};

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();

    const handleLogin = () => {
        navigate("/login");
    };

    return (
        <AuroraBackground theme="dark" showRadialGradient={true}>
            <div className="min-h-screen flex flex-col">
                {/* 배경 이미지 레이어 */}
                <div
                    className="absolute inset-0 z-0 opacity-40"
                    style={{
                        backgroundImage: "url('/aurora-church-bg.png')",
                        backgroundSize: "cover",
                        backgroundPosition: "center bottom",
                        backgroundRepeat: "no-repeat",
                    }}
                />

                {/* 십자가 빨간 불빛 */}
                <CrossLight />

                {/* 그라데이션 오버레이 - 하단을 더 어둡게 */}
                <div className="absolute inset-0 z-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

                {/* 메인 콘텐츠 */}
                <main className="relative z-10 flex-1 flex flex-col justify-center items-center px-4 py-12 md:py-20">
                    {/* Hero Section */}
                    <div className="text-center mb-12 md:mb-16 animate-float">
                        {/* 부제목 */}
                        <p className="text-cyan-400 text-sm md:text-base font-medium tracking-widest uppercase mb-4">
                            Budapest Korean Mission Church
                        </p>

                        {/* 메인 타이틀 */}
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            부다페스트한인선교교회
                            <br />
                            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                                행정 관리 시스템
                            </span>
                        </h1>

                        {/* 슬로건 */}
                        <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
                            은혜 가운데 함께 성장하는 공동체
                            <br className="hidden md:block" />
                            <span className="text-gray-400">
                                교적 · 심방 · 교육 · 재정을 한 곳에서 관리하세요
                            </span>
                        </p>

                        {/* CTA 버튼 */}
                        <button
                            onClick={handleLogin}
                            className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 
                       text-white font-semibold rounded-full 
                       hover:from-purple-500 hover:to-cyan-500 
                       transition-all duration-300 
                       shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50
                       hover:scale-105 active:scale-95"
                        >
                            <span className="flex items-center gap-2">
                                시작하기
                                <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </button>
                    </div>

                    {/* Features Section */}
                    <div className="w-full max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            {features.map((feature, index) => (
                                <FeatureCard
                                    key={feature.title}
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                    gradient={feature.gradient}
                                    delay={index * 100}
                                />
                            ))}
                        </div>
                    </div>
                </main>

                {/* Footer */}
                <footer className="relative z-10 text-center py-6 text-gray-500 text-sm">
                    <p>© 2024 부다페스트한인선교교회. All rights reserved.</p>
                </footer>
            </div>
        </AuroraBackground>
    );
};
